import React, { useMemo } from 'react';
import {Checkbox, CheckChangeContext} from 'tea-component';
import OptionItemInfo from './OptionItemInfo';
import { IGroupData, IOptionsGroupProps, TOptionsItem } from '../types';
import { List as VirtualizedList, AutoSizer } from 'react-virtualized';
import { intersection, uniq } from 'lodash-es';
import classnames from 'classnames';
import { sanitizeRichHtml } from '@sg/utils/sanitizeHtml';

const DISPLAY_TYPE_GROUP = 'group';
const DISPLAY_TYPE_OPTION = 'option';

const ROW_HEIGHT = 36;
const MAX_VISIBLE_ROWS = 10; // 根据需要显示的行数进行调整
const OptionsGroup = (props: IOptionsGroupProps) => {
  const {
    value: propsValue,
    isMatch,
    virtual,
    listHeight: propsHeight,
    options,
    formatMessage,
    onChange,
    group,
    groupByKey = 'type',
  } = props || {};

  const { groupList, renderOptions } = useMemo(() => {
    // 分组数据重组
    const groupList: IGroupData[] = [];
    const renderOptions: Array<IGroupData | TOptionsItem> = [];
    Object.keys(group || {}).forEach((key) => {
      const optList = options
        ?.filter((item) => item?.[groupByKey] === key)
        .map((item) => ({
          ...item,
          displayType: DISPLAY_TYPE_OPTION,
          checked: propsValue.includes(item.value),
        }));
      // 搜索的时候，展示高亮的分组名称
      // const highlightGroupName = optList?.[0]?.groupName;

      if (optList.length < 1) {
        return;
      }
      // 取出value中包含的group options的值
      const selectedValues = intersection(
        propsValue,
        optList.map((it) => it.value),
      );
      const groupItem: IGroupData = {
        displayType: DISPLAY_TYPE_GROUP,
        text: group?.[key] || '',
        value: key,
        // 计算是否全选
        checked: selectedValues.length === optList.length,
        // 计算是否半选
        halfChecked:
          selectedValues.length > 0 && selectedValues.length !== optList.length,
        disabled: optList.filter((it) => it.disabled).length === optList.length,
        optList,
      };
      groupList.push(groupItem);
    });

    // 是否有分组数据
    const hasGroup = groupList.length > 0;
    if (hasGroup) {
      // 平铺分组
      groupList.forEach((groupItem) => {
        const { optList } = groupItem;
        renderOptions.push(groupItem);

        optList.forEach((optItem) => {
          renderOptions.push(optItem);
        });
      });
      return {
        hasGroup,
        groupList,
        renderOptions,
      };
    }
    return {
      hasGroup,
      renderOptions: options.map((item) => ({
        ...item,
        checked: propsValue.includes(item.value),
      })),
    };
  }, [group, groupByKey, options, propsValue]);

  const handleChange = (check: boolean, item: IGroupData | TOptionsItem, context: CheckChangeContext) => {
    const { displayType = '', value } = item;
    if (displayType === DISPLAY_TYPE_GROUP) {
      const findGroup = groupList?.find(
        (groupItem) => groupItem.value === value,
      );
      const { optList = [] } = findGroup || {};
      // todo 此处未考虑max和min的对选择的影响，后续如果需要支持max和min需完善该功能
      const optValues = optList
        .filter((optItem) => optItem.disabled !== true)
        .map((item) => item.value);
      if (check) {
        onChange(uniq([...propsValue, ...optValues]), context);
      } else {
        onChange([...propsValue.filter((v) => !optValues.includes(v))], context);
      }
    } else {
      if (check) {
        onChange(uniq([...propsValue, value]), context);
      } else {
        onChange([...propsValue.filter((v) => v !== value)], context);
      }
    }
  };

  return (
    <>
      {isMatch ? (
        // 虚拟滚动隐藏外部滚动条
        <div
          className={classnames('db-filter-select-options-list', {
            'db-virtual': virtual,
          })}
          style={
            propsHeight ? { height: propsHeight, maxHeight: propsHeight } : {}
          }
        >
          {virtual ? (
            <div
              style={
                virtual
                  ? { height: `${renderOptions?.length * ROW_HEIGHT}px` }
                  : {}
              }
            >
              <AutoSizer>
                {({ width, height }) => {
                  // 根据可用高度计算可见行数
                  const visibleRowCount = Math.min(
                    Math.floor(height / ROW_HEIGHT),
                    MAX_VISIBLE_ROWS,
                  );
                  // 根据可见行数计算 VirtualizedList 的高度
                  const listHeight = propsHeight
                    ? propsHeight
                    : visibleRowCount * ROW_HEIGHT;
                  return (
                    <VirtualizedList
                      rowCount={renderOptions?.length}
                      rowRenderer={({ index, style }) => {
                        const item = renderOptions?.[index];
                        const {
                          value,
                          disabled = false,
                          checked,
                          halfChecked = false,
                          displayType,
                        } = item;
                        return (
                          <Checkbox
                            className={classnames({
                              'db-filter-select-group-option':
                                displayType === DISPLAY_TYPE_OPTION,
                            })}
                            style={style}
                            key={value}
                            value={checked}
                            name={value}
                            indeterminate={halfChecked}
                            disabled={disabled}
                            onChange={(check, context) => {
                              handleChange(check, item, context);
                            }}
                            title={item?.title ?? ''}
                          >
                            {item?.icon && <img src={item?.icon} />}
                            {item?.render?.(item) || (
                              <span
                                dangerouslySetInnerHTML={{
                                  __html: sanitizeRichHtml(`${item?.text}`),
                                }}
                              />
                            )}
                            <OptionItemInfo infoItem={item} />
                          </Checkbox>
                        );
                      }}
                      width={width}
                      height={listHeight}
                      rowHeight={ROW_HEIGHT}
                      overscanRowCount={5}
                      scrollToIndex={0}
                    />
                  );
                }}
              </AutoSizer>
            </div>
          ) : (
            <>
              {renderOptions?.map((item) => (
                <Checkbox
                  key={item?.value}
                  className={classnames({
                    'db-filter-select-group-option':
                      item?.displayType === DISPLAY_TYPE_OPTION,
                  })}
                  value={item?.checked}
                  name={item?.value}
                  indeterminate={item?.halfChecked}
                  disabled={item?.disabled}
                  onChange={(checked, context) => {
                    handleChange(checked, item, context);
                  }}
                >
                  {item?.icon && <img src={item?.icon} />}
                  {item?.render ? (
                    item?.render(item)
                  ) : (
                    <span
                      dangerouslySetInnerHTML={{
                        __html: sanitizeRichHtml(`${item?.text}`),
                      }}
                    />
                  )}
                  <OptionItemInfo infoItem={item} />
                </Checkbox>
              ))}
            </>
          )}
        </div>
      ) : (
        <div className="db-filter-no-result">
          {formatMessage({ id: 'no_result' })}
        </div>
      )}
    </>
  );
};

export default OptionsGroup;
